import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Toolbar
} from "@mui/material";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import EditWrap from "../../commonComponent/editWrap";

export const gaugeTypeOptions = [
  { id: 1, label: "Thread Plug Gauge" },
  { id: 2, label: "Thread Ring Gauge" }
];

export default function EditThreadRingPlug(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== "0");

  const [gaugeType, setGaugeType] = useState(null);
  const [specification, setSpecification] = useState("");

  // GO Major
  const [goMajorMax, setGoMajorMax] = useState("");
  const [goMajorMin, setGoMajorMin] = useState("");
  const [goMajorWearLimit, setGoMajorWearLimit] = useState("");

  // NOGO Major
  const [nogoMajorMax, setNogoMajorMax] = useState("");
  const [nogoMajorMin, setNogoMajorMin] = useState("");
  const [nogoMajorWearLimit, setNogoMajorWearLimit] = useState("");

  // GO Effective
  const [goEffectiveMax, setGoEffectiveMax] = useState("");
  const [goEffectiveMin, setGoEffectiveMin] = useState("");
  const [goEffectiveWearLimit, setGoEffectiveWearLimit] = useState("");

  // NOGO Effective
  const [nogoEffectiveMax, setNogoEffectiveMax] = useState("");
  const [nogoEffectiveMin, setNogoEffectiveMin] = useState("");
  const [nogoEffectiveWearLimit, setNogoEffectiveWearLimit] = useState("");

  useEffect(() => {
    if (isEditing) {
      fetchDetails();
    }
  }, [id]);

  const fetchDetails = async () => {
    try {
      const res = await axiosWithToken.get(`${BASE_URL}ThreadPlugRingSizes/${id}`);
      const data = Array.isArray(res.data) ? res.data[0] : res.data;
      if (data) {
        const foundType = gaugeTypeOptions.find((opt) => opt.id === Number(data.gaugeType)) || null;
        setGaugeType(foundType);
        setSpecification(data.specification || "");

        // GO Major
        setGoMajorMax(data.goMajorMax ?? "");
        setGoMajorMin(data.goMajorMin ?? "");
        setGoMajorWearLimit(data.goMajorWearLimit ?? "");

        // NOGO Major
        setNogoMajorMax(data.nogoMajorMax ?? "");
        setNogoMajorMin(data.nogoMajorMin ?? "");
        setNogoMajorWearLimit(data.nogoMajorWearLimit ?? "");

        // GO Effective
        setGoEffectiveMax(data.goEffectiveMax ?? data.goMax ?? "");
        setGoEffectiveMin(data.goEffectiveMin ?? data.goMin ?? "");
        setGoEffectiveWearLimit(data.goEffectiveWearLimit ?? data.goWearLimit ?? "");

        // NOGO Effective
        setNogoEffectiveMax(data.NogoEffectiveMax ?? data.nogoEffectiveMax ?? data.nogoMax ?? "");
        setNogoEffectiveMin(data.NogoEffectiveMin ?? data.nogoEffectiveMin ?? data.nogoMin ?? "");
        setNogoEffectiveWearLimit(data.NogoEffectiveWearLimit ?? data.nogoEffectiveWearLimit ?? data.nogoWearLimit ?? "");
      }
    } catch (error) {
      console.error("Error fetching Thread Plug/Ring details:", error);
      toast.error("Failed to load specification details");
    }
  };

  const handleSubmit = async () => {
    if (!gaugeType) {
      toast.error("Please select a Gauge Type");
      return;
    }
    if (!specification.trim()) {
      toast.error("Please enter a Standard Specification");
      return;
    }

    const payload = {
      gaugeType: gaugeType.id,
      specification: specification.trim(),

      goMajorMax: goMajorMax !== "" ? parseFloat(goMajorMax) : null,
      goMajorMin: goMajorMin !== "" ? parseFloat(goMajorMin) : null,
      goMajorWearLimit: goMajorWearLimit !== "" ? parseFloat(goMajorWearLimit) : null,

      nogoMajorMax: nogoMajorMax !== "" ? parseFloat(nogoMajorMax) : null,
      nogoMajorMin: nogoMajorMin !== "" ? parseFloat(nogoMajorMin) : null,
      nogoMajorWearLimit: nogoMajorWearLimit !== "" ? parseFloat(nogoMajorWearLimit) : null,

      goEffectiveMax: goEffectiveMax !== "" ? parseFloat(goEffectiveMax) : null,
      goEffectiveMin: goEffectiveMin !== "" ? parseFloat(goEffectiveMin) : null,
      goEffectiveWearLimit: goEffectiveWearLimit !== "" ? parseFloat(goEffectiveWearLimit) : null,

      NogoEffectiveMax: nogoEffectiveMax !== "" ? parseFloat(nogoEffectiveMax) : null,
      NogoEffectiveMin: nogoEffectiveMin !== "" ? parseFloat(nogoEffectiveMin) : null,
      NogoEffectiveWearLimit: nogoEffectiveWearLimit !== "" ? parseInt(nogoEffectiveWearLimit) : 0,
    };

    try {
      if (isEditing) {
        await axiosWithToken.patch(`${BASE_URL}ThreadPlugRingSizes/${id}`, payload);
        toast.success("Thread Ring/Plug updated successfully!");
      } else {
        await axiosWithToken.post(`${BASE_URL}ThreadPlugRingSizes`, payload);
        toast.success("Thread Ring/Plug created successfully!");
      }
      setTimeout(() => {
        navigate("/master/threadRingPlugList");
      }, 500);
    } catch (error) {
      console.error("Error saving Thread Plug/Ring:", error);
      toast.error("Failed to save");
    }
  };

  return (
    <EditWrap>
      {/* Page Title */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <Typography variant="h6" component="h6" style={{ float: "left" }}>
          {isEditing ? "Edit Thread Ring/Plug " : "Create New Thread Ring/Plug"}
        </Typography>
      </div>

      {/* Form Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={gaugeTypeOptions}
            getOptionLabel={(option) => option.label || ""}
            value={gaugeType}
            onChange={(e, newValue) => setGaugeType(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Gauge Type *" variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <TextField
            label="Standard Specification *"
            variant="outlined"
            size="small"
            fullWidth
            value={specification}
            onChange={(e) => setSpecification(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Limits Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 1 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#DDDDDD" }}>
            <TableRow>
              <TableCell sx={{ width: "22%" }}>
                <b>PARAMETER (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "26%" }}>
                <b>SPECIFIED LIMIT MAX (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "26%" }}>
                <b>SPECIFIED LIMIT MIN (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "26%" }}>
                <b>WEAR LIMIT (MM)</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* GO Major */}
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>GO Major</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goMajorMax}
                  onChange={(e) => setGoMajorMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goMajorMin}
                  onChange={(e) => setGoMajorMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goMajorWearLimit}
                  onChange={(e) => setGoMajorWearLimit(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            {/* NOGO Major */}
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>NOGO Major</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoMajorMax}
                  onChange={(e) => setNogoMajorMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoMajorMin}
                  onChange={(e) => setNogoMajorMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoMajorWearLimit}
                  onChange={(e) => setNogoMajorWearLimit(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            {/* GO Effective */}
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>GO Effective</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goEffectiveMax}
                  onChange={(e) => setGoEffectiveMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goEffectiveMin}
                  onChange={(e) => setGoEffectiveMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goEffectiveWearLimit}
                  onChange={(e) => setGoEffectiveWearLimit(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            {/* NOGO Effective */}
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>NOGO Effective</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoEffectiveMax}
                  onChange={(e) => setNogoEffectiveMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoEffectiveMin}
                  onChange={(e) => setNogoEffectiveMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoEffectiveWearLimit}
                  onChange={(e) => setNogoEffectiveWearLimit(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Toolbar */}
      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginTop: "3rem",
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "grey" }}
          size="small"
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={handleSubmit}
        >
          {isEditing ? "Update" : "Save"}
        </Button>
      </Toolbar>
      <ToastContainer />
    </EditWrap>
  );
}